/* @jsxImportSource @emotion/react */

import React from 'react'
import type { MessageDescriptor } from 'react-intl'
import { defineMessage, defineMessages, useIntl } from 'react-intl'

import handDrawnBorderBlack from '../../../assets/hand-drawn-border-black.svg'
import { mediaQuerySmall } from '../../../components/style'
import type { SliteCaseStudyData } from './+data'

type Impact = NonNullable<SliteCaseStudyData['impacts']>[number]

// Mirrors `impactTypeOptions` in the sanity-studio repo
// (schemaTypes/constants.ts), keyed by the stored value.
const IMPACT_TYPE_LABELS: Record<string, MessageDescriptor> = defineMessages({
  'time-saved': {
    id: 'website.customers.impactType.timeSaved',
    defaultMessage: 'Time saved',
  },
  'cost-savings': {
    id: 'website.customers.impactType.costSavings',
    defaultMessage: 'Cost savings',
  },
  'knowledge-access': {
    id: 'website.customers.impactType.knowledgeAccess',
    defaultMessage: 'Knowledge access',
  },
  onboarding: {
    id: 'website.customers.impactType.onboarding',
    defaultMessage: 'Onboarding',
  },
  alignment: {
    id: 'website.customers.impactType.alignment',
    defaultMessage: 'Team alignment',
  },
  adoption: {
    id: 'website.customers.impactType.adoption',
    defaultMessage: 'Adoption',
  },
})

const UNTYPED_IMPACT_LABEL = defineMessage({
  id: 'website.customers.impactType.untyped',
  defaultMessage: 'Impact',
})

const COLUMN_GUTTER = 40

const listCss = {
  gridAutoFlow: 'column',
  gridAutoColumns: 'minmax(0, 1fr)',
  [mediaQuerySmall]: { gridAutoFlow: 'row' },
} as const

const DIVIDER_INSET = 16

// A pseudo-element, not a border, so the divider can stop short of the ends.
const itemCss = {
  padding: `8px ${COLUMN_GUTTER}px`,
  marginLeft: 4,
  '&:first-of-type': { paddingLeft: 0 },
  '&:last-of-type': { paddingRight: 0 },
  '& + &::before': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: DIVIDER_INSET,
    bottom: DIVIDER_INSET,
    width: 1,
    background: 'var(--alpha-shade-45)',
  },
  [mediaQuerySmall]: {
    padding: '24px 0',
    '&:first-of-type': { paddingTop: 0 },
    '&:last-of-type': { paddingBottom: 0 },
    '& + &::before': {
      top: 0,
      bottom: 'auto',
      right: 0,
      width: 'auto',
      height: 1,
    },
  },
} as const

// Same pencil outline as the secondary button, at pill size.
const pillCss = {
  padding: '3px 10px',
  border: '2px solid transparent',
  borderImage: `url("${handDrawnBorderBlack}") 20 / 14px / 0px`,
  lineHeight: '19px',
} as const

const numberCss = {
  lineHeight: 1.1,
  letterSpacing: '-0.01em',
} as const

const textCss = {
  maxWidth: 320,
  lineHeight: 1.45,
  textWrap: 'pretty',
} as const

export function CaseStudyImpacts(props: { impacts: Impact[] }) {
  const intl = useIntl()

  return (
    <ul className="grid padding-vertical-16" css={listCss}>
      {props.impacts.map((impact) => {
        const typeLabel =
          (impact.type && IMPACT_TYPE_LABELS[impact.type]) ||
          UNTYPED_IMPACT_LABEL
        return (
          <li key={impact._key} className="position-relative" css={itemCss}>
            <span
              className="inline-block font-size-7 font-weight-semibold shade-00"
              css={pillCss}
            >
              {intl.formatMessage(typeLabel)}
            </span>
            <div
              className="margin-top-24 font-family-title font-weight-medium font-size-2 shade-00"
              css={numberCss}
            >
              {impact.number}
            </div>
            <p className="margin-top-12 font-size-5 shade-00" css={textCss}>
              {impact.text}
            </p>
          </li>
        )
      })}
    </ul>
  )
}
